import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaLinkedin,
  FaTwitter,
  FaFacebook,
  FaInstagram,
} from "react-icons/fa";
import logoImage from "../assets/wata_logo.png";

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  color: white;
  padding: 4rem 0 2rem 0;

  @media (max-width: 768px) {
    padding: 3rem 0 2rem 0;
  }

  @media (max-width: 480px) {
    padding: 2rem 0 1rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 2rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const FooterSection = styled.div`
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: #005d3b;
  }

  p {
    color: #ccc;
    line-height: 1.6;
    margin-bottom: 1rem;
  }

  ul {
    list-style: none;

    li {
      margin-bottom: 0.75rem;

      a {
        color: #ccc;
        text-decoration: none;
        transition: color 0.3s ease;

        &:hover {
          color: #005d3b;
        }
      }
    }
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;

  img {
    height: 60px;
    width: auto;
    filter: brightness(0) invert(1);
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }
  }

  @media (max-width: 768px) {
    img {
      height: 50px;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;

  a {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    text-decoration: none;
    transition: all 0.3s ease;

    &:hover {
      background: #005d3b;
      transform: translateY(-2px);
    }
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  color: #ccc;

  .icon {
    margin-right: 0.75rem;
    color: #005d3b;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid #444;
  padding-top: 2rem;
  text-align: center;
  color: #999;

  p {
    margin: 0;
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId) => {
    // If we're not on the homepage, navigate to homepage first
    if (location.pathname !== "/") {
      navigate("/");
      // Wait for navigation to complete, then scroll to section
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      // We're on homepage, scroll directly to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const quickLinks = [
    { name: "About Us", sectionId: "about" },
    { name: "Services", sectionId: "services" },
    { name: "Community", sectionId: "community" },
    { name: "Contact", sectionId: "contact" },
  ];

  const legalLinks = [
    { name: "Privacy Policy", href: "/privacy-policy" },
    { name: "Terms & Conditions", href: "/terms-and-conditions" },
  ];

  const services = [
    { name: "Community Building", sectionId: "services" },
    { name: "Voice & Dialogue", sectionId: "services" },
    { name: "Accountability Tools", sectionId: "services" },
    { name: "Impact Analytics", sectionId: "services" },
  ];

  const socialLinks = [
    { icon: <FaLinkedin />, href: "#", label: "LinkedIn" },
    { icon: <FaTwitter />, href: "#", label: "Twitter" },
    { icon: <FaFacebook />, href: "#", label: "Facebook" },
    { icon: <FaInstagram />, href: "#", label: "Instagram" },
  ];

  return (
    <FooterContainer>
      <Container>
        <FooterContent>
          <FooterSection>
            <Logo>
              <img src={logoImage} alt="WATA Logo" />
            </Logo>
            <p>
              We Are The Answer - empowering Africans to build accountable
              communities through digital solutions that turn conversations into
              action and voices into change.
            </p>
            {/* <SocialLinks>
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {social.icon}
                </a>
              ))}
            </SocialLinks> */}
          </FooterSection>

          <FooterSection>
            <h3>Quick Links</h3>
            <ul>
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.sectionId);
                    }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </FooterSection>

          <FooterSection>
            <h3>Services</h3>
            <ul>
              {services.map((service, index) => (
                <li key={index}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(service.sectionId);
                    }}
                  >
                    {service.name}
                  </a>
                </li>
              ))}
            </ul>
          </FooterSection>

          <FooterSection>
            <h3>Legal</h3>
            <ul>
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.href}>{link.name}</Link>
                </li>
              ))}
            </ul>
          </FooterSection>

          <FooterSection>
            <h3>Contact Info</h3>
            <ContactInfo>
              <FaEnvelope className="icon" />
              <span>info@wata-africa.com</span>
            </ContactInfo>
            <ContactInfo>
              <FaPhone className="icon" />
              <span>+256 705813875</span>
            </ContactInfo>
            <ContactInfo>
              <FaMapMarkerAlt className="icon" />
              <span>1 Musigire close, Bweyogerere, Kampala</span>
            </ContactInfo>
          </FooterSection>
        </FooterContent>

        <FooterBottom>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            © {currentYear} Bon Africa Solutions. All rights reserved.
          </motion.p>
        </FooterBottom>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
